import { ListInlineIconItem, ListInlineIconProps } from 'common/grid-list-items/list-inline-icon-item.js';
import { IconPropsAll } from 'common/icon/icon.js';
import * as React from 'react';
import { MarkerTypeWaterActivity } from 'types/markerTypes/location-markers-type.js';
import { CellStatusRating, IDataCell_StatusRating } from 'utilities/buildTables/cell-build-data/buildDataStatus.js';
import { TIME_FORMATS, formatDate } from 'utilities/date.js';
import { isNotNullOrUndefined } from 'utilities/isType.js';
import { getDefaultWaterMarkerIconName } from '../../components/map/marker-view/collections/default-marker-behaviour/utils.js';
import { getMarineWeatherRisk } from '../../components/map/marker-view/collections/marine-marker/utils.js';

export type MarineRiskProps = {
  risk?: CellStatusRating;
  date?: string;
  weatherRiskKayak?: CellStatusRating;
  weatherRiskPaddleboarding?: CellStatusRating;
  windKm?: CellStatusRating;
  gustKm?: CellStatusRating;
  waveHeight?: CellStatusRating;
  windDirection?: CellStatusRating;
};

const getForecastistProps = ({
  itemPrimary,
  itemSecondary,
  title,
  ...iconProps
}: IconPropsAll & {
  itemPrimary?: IDataCell_StatusRating<'Render'>;
  itemSecondary?: IDataCell_StatusRating<'Render'>;
  title: string;
}): ListInlineIconProps | undefined => {
  if (itemPrimary) {
    const { text: value, status: statusOne } = itemPrimary;
    const { text: valueSecondary, status: statusTwo } = itemSecondary ?? {};

    const el1 = (
      <span key="el1" className={`u-text-sm u-textSemibold ${statusOne ?? ''}`}>
        {value}
      </span>
    );
    const el2 = (
      <span key="el2" className={`u-text-sm u-textSemibold ${statusTwo ?? ''}`}>
        {valueSecondary}
      </span>
    );

    if (value && valueSecondary) {
      return {
        ...iconProps,
        title,
        children: [el1, el2],
      };
    }
    if (value) {
      return {
        ...iconProps,
        title,
        children: [el1],
      };
    }
  }
  return undefined;
};

export const MarineMarkerDescription = ({
  value,
  type,
}: {
  value: MarineRiskProps;
  type: typeof MarkerTypeWaterActivity.Paddleboarding | typeof MarkerTypeWaterActivity.Kayak;
}) => {
  const { windKm, gustKm, waveHeight, risk, date, windDirection } = value;
  const weatherRisk = getMarineWeatherRisk(type, value);

  const els = [
    getForecastistProps({
      v: 1,
      icon: getDefaultWaterMarkerIconName(type),
      title: 'Weather risk',
      itemPrimary: weatherRisk,
    }),
    getForecastistProps({
      v: 1,
      icon: 'wind',
      title: 'Wind',
      itemPrimary: windKm,
      itemSecondary: windDirection,
    }),
    getForecastistProps({ v: 1, icon: 'gust', title: 'Gusts', itemPrimary: gustKm }),
    getForecastistProps({ v: 1, icon: 'wave-height', title: 'Wave Height', itemPrimary: waveHeight }),
  ].filter(isNotNullOrUndefined);

  if (els.length > 0) {
    return (
      <MarineMarkerWrapper>
        {date && (
          <small className="u-textGrey u-inlineBlock fineprint u-mB-sm">{`Forecast for ${formatDate(
            date,
            TIME_FORMATS.HourAMPM,
          )}`}</small>
        )}
        <div className="grid-list">
          {els.map((props, i) => {
            return <ListInlineIconItem key={i} {...props} />;
          })}
        </div>
        {risk && <p className="u-mT-xs">{risk.text}</p>}
      </MarineMarkerWrapper>
    );
  }
  return <MarineMarkerWrapper key={risk?.text}>{risk && <p className="u-mT-xs">{risk.text}</p>}</MarineMarkerWrapper>;
};

const MarineMarkerWrapper = ({ children }: { children: React.ReactNode }) => {
  return <div className="u-mT-sm u-mB-sm">{children}</div>;
};
